
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const usePasswordManagement = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<PasswordFormData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  // Check if user is OAuth-only (no email provider in their providers list)
  const isOAuthOnly = () => {
    if (!user?.app_metadata?.providers) return false;
    const providers = user.app_metadata.providers as string[];
    return !providers.includes('email');
  };

  // Password validation
  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: minLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar,
      requirements: {
        minLength,
        hasUppercase,
        hasLowercase,
        hasNumber,
        hasSpecialChar
      }
    };
  };

  // Validate form
  const validateForm = () => {
    if (!isOAuthOnly() && !formData.currentPassword.trim()) {
      toast.error("Current password is required");
      return false;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      toast.error("Password does not meet requirements");
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }

    return true;
  };

  // Set password for OAuth users
  const setPassword = async () => {
    if (!validateForm()) return false;

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (error) throw error;

      toast.success("Password set successfully! You can now log in with email and password.");
      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      return true;
    } catch (error: any) {
      console.error("Error setting password:", error);
      toast.error(error.message || "Failed to set password");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Change password for existing users
  const changePassword = async () => {
    if (!validateForm()) return false;

    setIsLoading(true);
    try {
      // First verify current password by attempting to sign in
      if (!user?.email) {
        throw new Error("User email not found");
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: formData.currentPassword
      });

      if (signInError) {
        throw new Error("Current password is incorrect");
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (error) throw error;

      toast.success("Password changed successfully!");
      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      return true;
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast.error(error.message || "Failed to change password");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (field: keyof PasswordFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return {
    formData,
    isLoading,
    isOAuthOnly: isOAuthOnly(),
    validatePassword,
    setPassword,
    changePassword,
    updateFormData
  };
};
